// NEW_FILE_JUSTIFIED: New hook for the insurance type builder list view — groups org insurance types by provider; distinct from useInsuranceTypeEditor (single type editing) and useInsuranceTypeSelection (policy form selection)
import { useMemo } from 'react';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package required for useQuery — not a convex backend module
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import { useOrgContext } from '@/hooks/useOrgContext';
import type {
  ITypeListEntry,
  ITypeListGroup,
  IUseInsuranceTypeListResult,
} from '@/@types/insuranceBuilder';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import

const UNASSIGNED_LABEL = 'Unassigned';

export function useInsuranceTypeList(): IUseInsuranceTypeListResult {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const rawTypes = useQuery(
    api['insurance/types'].listByProvider,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const loading = orgLoading || rawTypes === undefined;

  return useMemo((): IUseInsuranceTypeListResult => {
    if (loading || !rawTypes) {
      return { groups: [], loading, hasOrphans: false };
    }

    const groupMap = new Map<string, ITypeListGroup>();

    for (const raw of rawTypes) {
      const entry: ITypeListEntry = {
        _id: raw._id,
        orgId: raw.orgId,
        providerId: raw.providerId,
        providerName: raw.providerName,
        name: raw.name,
        supportsDrivers: raw.supportsDrivers,
        sectionCount: raw.sectionCount,
        fieldCount: raw.fieldCount,
        policyCount: raw.policyCount,
        updatedAt: raw.updatedAt,
      };

      if (raw.providerId) {
        const key = raw.providerId;
        const existing = groupMap.get(key);
        if (existing) {
          existing.types.push(entry);
        } else {
          groupMap.set(key, {
            providerId: raw.providerId as Id<'insuranceProviders'>, // AS_JUSTIFIED: Convex query returns v.id('insuranceProviders') string; Map key is string but group shape requires typed Id
            providerName: raw.providerName ?? raw.providerId,
            types: [entry],
          });
        }
      } else {
        const existing = groupMap.get(UNASSIGNED_LABEL);
        if (existing) {
          existing.types.push(entry);
        } else {
          groupMap.set(UNASSIGNED_LABEL, {
            providerId: null,
            providerName: UNASSIGNED_LABEL,
            types: [entry],
          });
        }
      }
    }

    const unassignedGroup = groupMap.get(UNASSIGNED_LABEL);
    const hasOrphans = unassignedGroup !== undefined && unassignedGroup.types.length > 0;

    // Named provider groups sorted alphabetically; Unassigned appended last
    const namedGroups = Array.from(groupMap.values())
      .filter(g => g.providerId !== null)
      .sort((a, b) => a.providerName.localeCompare(b.providerName));

    for (const group of namedGroups) {
      group.types.sort((a, b) => a.name.localeCompare(b.name));
    }

    if (unassignedGroup) {
      unassignedGroup.types.sort((a, b) => a.name.localeCompare(b.name));
    }

    const groups = unassignedGroup ? [...namedGroups, unassignedGroup] : namedGroups;

    return { groups, loading: false, hasOrphans };
  }, [rawTypes, loading]);
}
